import React, { useEffect, useState } from 'react';

import { Box, Button, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Modal, Select, Skeleton, TextField, Typography, styled } from '@mui/material';
import Checkout from '../components/Checkout';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import Markdown from 'react-markdown';
import { ShoppingCart } from '@mui/icons-material';
import { Cart, CategoryType, Item } from '../types';
import { fetchProduct } from '../utils/api';
import { isStoreOpen } from '../utils/store';

const Panel = styled(Box)(({ theme }) => ({
  boxSizing: "border-box",
  display: "inline-flex",
  height: "100%",
  verticalAlign: "top",
  width: "50%",
  minWidth: 0,
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const LeftPanel = styled(Panel)(({ theme }) => ({
  alignItems: "stretch",
  backgroundColor: theme.palette.background.paper,
  flexDirection: "column",
  gap: theme.spacing(1.5),
}));

const RightPanel = styled(Panel)(({ theme }) => ({
  alignItems: "center",
  justifyContent: "center",
}));

const ProductView = () => {
  const shopOpen = isStoreOpen();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();

  const clientSecret = searchParams.get("payment_intent_client_secret");

  const localCart = localStorage.getItem("cart")
  
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<Error>();
  const [selectedVariation, setSelectedVariation] = useState<Item>();
  const [quantity, setQuantity] = useState(1);
  const [cart, setCart] = useState<Cart>(localCart ? JSON.parse(localCart) : []);
  const [isImageLoaded, setImageLoaded] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isCartOpen, setIsCartOpen] = useState(false);


  const { data: product } = useQuery(["fetchProduct", slug], () => fetchProduct(slug || ""), {
    enabled: Boolean(slug),
    onSuccess: (res) => {
      if (!selectedVariation) setSelectedVariation(res.variations.find(variation => variation.inventory));
    }
  });
  const variations = product?.variations;

  const getPrice = (category: CategoryType) => {
    return category === "Youth" ? product?.priceYouth : product?.priceAdult;
  }

  const handleNextImage = () => {
    if (!product?.images) return;

    setCurrentImageIndex((prevIndex) =>
      prevIndex + 1 < product?.images.length ? prevIndex + 1 : prevIndex
    );
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0));
  };

  const updateCart = (newCart: Cart) => {
    setCart(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
  }

  const addToCart = () => {
    if (!shopOpen || !product || !selectedVariation) return;

    const itemId = `${slug}_${selectedVariation.category}_${selectedVariation.size}`
    const existingSizeIndex = cart.findIndex(item => item.id === itemId);
    const tempCart = JSON.parse(JSON.stringify([...cart]));

    if (existingSizeIndex >= 0) {
      tempCart[existingSizeIndex].quantity += quantity;
    } else {
      tempCart.push({
        quantity,
        id: itemId,
      })
    }

    updateCart(tempCart);

    setSelectedVariation(product.variations.find(variation => variation.inventory))
    setQuantity(1);
    setIsCartOpen(true);
  };

  const isSoldOut = product && !product.variations.reduce((a, b) => a + (b.inventory || b.quantity || 0), 0);

  const buttonAddToCart = (
    <Button
      fullWidth
      onClick={addToCart}
      disabled={!shopOpen || !selectedVariation?.inventory || !quantity}
      variant="contained"
      size="medium"
      sx={{ py: 1, fontWeight: 600 }}
    >
      Add to cart
    </Button>
  );

  useEffect(() => {
      if (clientSecret) navigate(`/confirmation/${clientSecret}`);
  }, [clientSecret]);

  if (error) {
    return <span>Error: {error.message}</span>;
  }

  const pauseNotice = (
    <Typography color="text.secondary" variant="body2" sx={{ width: "100%", textAlign: { xs: "center", sm: "left" } }}>
      The shop is paused — purchasing is unavailable.
    </Typography>
  );

  const markdownSx = {
    "& p": { mb: 1.25, fontSize: "0.9375rem", lineHeight: 1.6, color: "text.primary" },
    "& p:last-child": { mb: 0 },
    "& ul, & ol": { my: 1, pl: 2.25, fontSize: "0.9375rem" },
    "& li": { mb: 0.5 },
    "& h1, & h2, & h3": { fontSize: "1.05rem", fontWeight: 600, mt: 1.5, mb: 0.75 },
    "& strong": { fontWeight: 600 },
  } as const;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        maxWidth: 1100,
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        px: { xs: 1.5, sm: 2 },
      }}
    >
      <Box
        component="header"
        sx={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          mb: { xs: 1.5, sm: 2 },
        }}
      >
        <Button
          variant="text"
          href="/products"
          size="small"
          sx={{ minWidth: 44, px: 0.5, color: "text.primary" }}
          aria-label="Back to products"
        >
          <ChevronLeftIcon sx={{ mr: -0.5 }} />
          <HomeIcon fontSize="small" />
        </Button>
        <Box component="img" src="/assets/dreamlogo.png" alt="Inclusiviteee" sx={{ height: 40, width: "auto", display: "block" }} />
        <Box sx={{ width: 44, display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="small"
            onClick={() => shopOpen && setIsCartOpen(true)}
            sx={{ display: shopOpen && cart.length ? "inline-flex" : "none", minWidth: 0 }}
          >
            <ShoppingCart fontSize="small" />
            <Typography component="span" variant="caption" sx={{ ml: 0.25 }}>
              ({cart.reduce((a, b) => a + b.quantity, 0)})
            </Typography>
          </Button>
        </Box>
      </Box>

      <Box
        display="flex"
        sx={{
          flexDirection: { xs: "column", sm: "row" },
          flex: 1,
          minHeight: 0,
          gap: { xs: 0, sm: 2 },
        }}
      >
        <LeftPanel
          sx={{
            flex: { sm: 1 },
            minWidth: 0,
            overflow: { xs: "visible", sm: "hidden" },
            px: { xs: 0.5, sm: 2 },
            pb: { xs: 2, sm: 0 },
          }}
        >
          <Box
            width="100%"
            display="flex"
            flexDirection="column"
            flexGrow={1}
            sx={{
              minHeight: { xs: "auto", sm: 0 },
              flex: { sm: 1 },
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              {product ? (
                <>
                  <Typography
                    fontFamily="Bodoni Moda SC, sans-serif"
                    component="h1"
                    fontWeight={700}
                    sx={{
                      fontSize: { xs: "1.65rem", sm: "1.85rem" },
                      lineHeight: 1.15,
                      letterSpacing: "0.02em",
                      mb: 0.75,
                    }}
                  >
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Youth ${product.priceYouth} · Adult ${product.priceAdult}
                  </Typography>
                </>
              ) : (
                <>
                  <Skeleton variant="text" width="85%" height={36} />
                  <Skeleton variant="text" width={120} height={22} sx={{ mt: 0.5 }} />
                </>
              )}
            </Box>

            <Box
              sx={{
                display: { xs: "flex", sm: "none" },
                flexDirection: "column",
                gap: 1,
                alignItems: "stretch",
              }}
            >
              {!shopOpen && pauseNotice}
              <Box display="flex" flexWrap="wrap" gap={1} justifyContent="center" alignItems="flex-end">
                  {!isSoldOut ? (
                    <>
                      <FormControl sx={{ width: 108, minWidth: 108 }} size="small">
                      <InputLabel id="size-select-label-mobile">Size</InputLabel>
                        <Select
                            disabled={!shopOpen}
                            labelId="size-select-label-mobile"
                            id="size-select-mobile"
                            value={`${slug}_${selectedVariation?.category}_${selectedVariation?.size}`}
                            label="Size"
                            onChange={(e) => {
                              const [, category, size] = e.target.value.split('_');
                              const variation = product?.variations.find(v => v.category === category && v.size === size)

                              setSelectedVariation(variation);
                            }}
                            renderValue={() => {
                              return <Typography>{`${selectedVariation?.category} ${selectedVariation?.size}`}</Typography>
                            }}
                        >
                            {variations?.map((variation: Item) => (
                              <MenuItem disabled={!variation.inventory} key={`${variation?.category}_${variation?.size}`} value={`${slug}_${variation?.category}_${variation?.size}`} sx={{ color: ({ palette }) => variation.inventory ? palette.text.primary : palette.text.disabled}}>
                                <Box mr={1}>{`${variation.category} ${variation.size} $${getPrice(variation.category)}`}</Box>
                                {variation.inventory === 1 && <Box ml="auto">{`${variation.inventory} left!`}</Box>}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <TextField
                        disabled={!shopOpen}
                        size="small"
                        InputProps={{ inputProps: { min: 1, max: selectedVariation?.inventory } }}
                        label="Qty"
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(+e.target.value)}
                        sx={{ width: 72 }}
                      />
                    </>
                  ) : (
                    <Typography variant="body2">Sold out</Typography>
                  )}
                </Box>
                <Box sx={{ width: "100%", maxWidth: 360, mx: "auto" }}>{buttonAddToCart}</Box>
              </Box>

              <Box flexDirection="column" sx={{ display: { xs: "flex", sm: "none" }, gap: 0.5 }}>
                {product ? (
                  <>
                    <Box
                      component="img"
                      sx={{
                        width: "100%",
                        maxHeight: { xs: "min(42vh, 260px)", sm: 260 },
                        objectFit: "cover",
                        borderRadius: 1,
                        display: "block",
                        opacity: isImageLoaded ? 1 : 0,
                        transition: "opacity 0.35s ease",
                      }}
                      src={product.images[currentImageIndex]}
                      alt={`${product.name} — ${currentImageIndex + 1}`}
                      onLoad={() => setImageLoaded(true)}
                    />

                    <Box display="flex" justifyContent="center" py={0.5}>
                      <IconButton size="small" onClick={handlePrevImage} disabled={currentImageIndex === 0} aria-label="Previous image">
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={handleNextImage} disabled={currentImageIndex === product.images.length - 1} aria-label="Next image">
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </>
                ) : (
                  <CircularProgress size={28} sx={{ alignSelf: "center", my: 2 }} />
                )}
              </Box>

              <Box
                sx={{
                  flex: { sm: 1 },
                  minHeight: { sm: 0 },
                  display: "flex",
                  flexDirection: "column",
                  overflow: "auto",
                  py: { xs: 0.5, sm: 0 },
                  borderTop: { sm: "1px solid" },
                  borderColor: "divider",
                  mt: { xs: 0.5, sm: 0 },
                }}
              >
                <Box sx={markdownSx}>
                  <Markdown>{product?.description}</Markdown>
                </Box>
              </Box>

              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  flexDirection: "column",
                  gap: 1,
                  pt: 2,
                  mt: "auto",
                  flexShrink: 0,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                {!shopOpen && pauseNotice}
                <Box display="flex" flexWrap="wrap" gap={1} alignItems="flex-end" justifyContent="flex-start">
                  {!isSoldOut ? (
                    <>
                      <FormControl sx={{ width: 112, minWidth: 112 }} size="small">
                        <InputLabel id="size-select-label-desktop">Size</InputLabel>
                        <Select
                          disabled={!shopOpen}
                          labelId="size-select-label-desktop"
                          id="size-select-desktop"
                          value={`${slug}_${selectedVariation?.category}_${selectedVariation?.size}`}
                          label="Size"
                          onChange={(e) => {
                            const [, category, size] = e.target.value.split("_");
                            const variation = product?.variations.find((v) => v.category === category && v.size === size);
                            setSelectedVariation(variation);
                          }}
                          renderValue={() => (
                            <Typography variant="body2">{`${selectedVariation?.category} ${selectedVariation?.size}`}</Typography>
                          )}
                        >
                          {variations?.map((variation: Item) => (
                            <MenuItem
                              disabled={!variation.inventory}
                              key={`${variation?.category}_${variation?.size}`}
                              value={`${slug}_${variation?.category}_${variation?.size}`}
                              sx={{
                                color: ({ palette }) =>
                                  variation.inventory ? palette.text.primary : palette.text.disabled,
                              }}
                            >
                              <Box mr={1}>{`${variation.category} ${variation.size} $${getPrice(variation.category)}`}</Box>
                              {variation.inventory === 1 && (
                                <Box ml="auto">{`${variation.inventory} left!`}</Box>
                              )}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        disabled={!shopOpen}
                        size="small"
                        InputProps={{ inputProps: { min: 1, max: selectedVariation?.inventory } }}
                        label="Qty"
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(+e.target.value)}
                        sx={{ width: 72 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 140 }}>{buttonAddToCart}</Box>
                    </>
                  ) : (
                    <Typography variant="body2">Sold out</Typography>
                  )}
                </Box>
              </Box>
            </Box>
        </LeftPanel>
        <RightPanel
          flexDirection="column"
          sx={{
            flex: { sm: 1 },
            minWidth: 0,
            display: { xs: "none", sm: "flex" },
            height: "100%",
            minHeight: { sm: 440 },
            px: { sm: 1 },
            pb: 1,
            overflow: "hidden",
          }}
        >
          {product ? (
            <>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  width: "100%",
                  borderRadius: 1,
                  overflow: "hidden",
                  bgcolor: "grey.100",
                }}
              >
                <Box
                  component="img"
                  sx={{
                    width: "100%",
                    height: "100%",
                    maxHeight: { sm: "calc(100vh - 140px)" },
                    objectFit: "contain",
                    display: "block",
                    opacity: isImageLoaded ? 1 : 0,
                    transition: "opacity 0.35s ease",
                  }}
                  src={product.images[currentImageIndex]}
                  alt={`${product.name} — ${currentImageIndex + 1}`}
                  onLoad={() => setImageLoaded(true)}
                />
              </Box>

              <Box height={36} width="100%" display="flex" alignItems="center" justifyContent="center" pt={0.5}>
                <IconButton size="small" onClick={handlePrevImage} disabled={currentImageIndex === 0} aria-label="Previous image">
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleNextImage} disabled={currentImageIndex === product.images.length - 1} aria-label="Next image">
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </>
          ) : (
            <CircularProgress size={32} />
          )}
        </RightPanel>
      </Box>
      <Modal open={shopOpen && isCartOpen} onClose={() => setIsCartOpen(false)} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div>
          <Checkout activeStep={activeStep} cart={cart} setActiveStep={setActiveStep} setCart={updateCart} setIsCartOpen={setIsCartOpen} />
        </div>
      </Modal>
    </Box>
  );
};

export default ProductView;
